import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EditorContent, useEditor } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import StarterKit from "@tiptap/starter-kit";
import { TextAlign } from "@tiptap/extension-text-align";
import { TextStyleKit } from "@tiptap/extension-text-style";
import { Highlight } from "@tiptap/extension-highlight";
import { Link } from "@tiptap/extension-link";
import { Subscript } from "@tiptap/extension-subscript";
import { Superscript } from "@tiptap/extension-superscript";
import { TaskItem, TaskList } from "@tiptap/extension-list";
import { TableKit } from "@tiptap/extension-table";
import { CharacterCount, Placeholder } from "@tiptap/extensions";
import { Details, DetailsContent, DetailsSummary } from "@tiptap/extension-details";
import { Typography } from "@tiptap/extension-typography";
import {
  AlignCenter,
  AlignJustify,
  AlignLeft,
  AlignRight,
  Bold,
  Code,
  Eraser,
  FileCode2,
  Highlighter,
  IndentDecrease,
  IndentIncrease,
  Italic,
  Link2,
  Link2Off,
  List,
  ListChecks,
  ListCollapse,
  ListOrdered,
  Maximize2,
  Minimize2,
  Minus,
  PaintBucket,
  Quote,
  Redo2,
  Search,
  Strikethrough,
  Subscript as SubIcon,
  Superscript as SuperIcon,
  Table2,
  Underline as UnderlineIcon,
  Undo2,
  WrapText,
} from "lucide-react";
import { Indent, OptionalDir } from "./nodes";
import { EditorPromptModal } from "./components/EditorPromptModal";
import "./rich-text-editor.css";

const FONT_SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "28px", "32px", "40px"];
const LINE_HEIGHTS = ["1", "1.15", "1.5", "1.75", "2", "2.2"];
const TEXT_COLORS = [
  { css: "var(--rte-c-brand)", hex: "#982598", label: "Brand" },
  { css: "var(--rte-c-danger)", hex: "#dc2626", label: "Red" },
  { css: "var(--rte-c-warning)", hex: "#ea580c", label: "Orange" },
  { css: "var(--rte-c-gold)", hex: "#ca8a04", label: "Gold" },
  { css: "var(--rte-c-success)", hex: "#16a34a", label: "Green" },
  { css: "var(--rte-c-info)", hex: "#2563eb", label: "Blue" },
];
const HIGHLIGHT_COLORS = [
  { css: "var(--rte-hl-yellow)", hex: "#fde68a", label: "Yellow" },
  { css: "var(--rte-hl-green)", hex: "#bbf7d0", label: "Green" },
  { css: "var(--rte-hl-blue)", hex: "#bfdbfe", label: "Blue" },
  { css: "var(--rte-hl-pink)", hex: "#fbcfe8", label: "Pink" },
  { css: "var(--rte-hl-purple)", hex: "#ddd6fe", label: "Purple" },
  { css: "var(--rte-hl-red)", hex: "#fecaca", label: "Red" },
  { css: "var(--rte-hl-gray)", hex: "#e5e7eb", label: "Gray" },
  { css: "var(--rte-hl-white)", hex: "#ffffff", label: "White" },
];
const SPECIAL_CHARS = [
  { label: "— em", value: "—" },
  { label: "– en", value: "–" },
  { label: "…", value: "…" },
  { label: "•", value: "•" },
  { label: "·", value: "·" },
  { label: "©", value: "©" },
  { label: "®", value: "®" },
  { label: "™", value: "™" },
  { label: "✓", value: "✓" },
  { label: "×", value: "×" },
  { label: "÷", value: "÷" },
  { label: "±", value: "±" },
  { label: "°", value: "°" },
  { label: "NBSP", value: "\u00A0" },
];

const btnBase =
  "inline-flex h-7 min-w-7 items-center justify-center rounded-md px-1.5 text-[#15173d] transition-colors disabled:cursor-not-allowed disabled:opacity-35";
const btnIdle = "hover:bg-[#f3eef3]";
const btnActive = "bg-[#982598] text-white hover:bg-[#7d1d7d]";
const selectClass =
  "h-7 max-w-[118px] rounded-md border border-gray-200 bg-white px-1.5 text-[11px] font-semibold text-[#15173d] outline-none hover:border-[#982598]";
const divider = "mx-0.5 h-5 w-px shrink-0 bg-gray-200";

function ToolbarButton({ active, disabled, title, onClick, children, className = "" }) {
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={`${btnBase} ${active ? btnActive : btnIdle} ${className}`}
    >
      {children}
    </button>
  );
}

function currentHeading(editor) {
  for (let level = 1; level <= 6; level += 1) {
    if (editor.isActive("heading", { level })) return String(level);
  }
  return "p";
}

function toHexColor(value, fallback) {
  if (!value || typeof value !== "string") return fallback;
  const raw = value.trim();
  if (/^#([0-9a-f]{3})$/i.test(raw)) {
    return `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
  }
  if (/^#([0-9a-f]{6})$/i.test(raw)) return raw.toLowerCase();
  const rgb = raw.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    const hex = (n) => Number(n).toString(16).padStart(2, "0");
    return `#${hex(rgb[1])}${hex(rgb[2])}${hex(rgb[3])}`;
  }
  return fallback;
}

function ColorControl({ title, icon, value, fallback, colors, onChange, onClear }) {
  const current = colors.find((color) => color.css === value || color.hex === value);
  const hex = current?.hex || toHexColor(value, fallback);
  return (
    <span className="inline-flex items-center" onMouseDown={(e) => e.preventDefault()}>
      <span className="gp-rte-color" title={title}>
        {icon}
        <span className="gp-rte-color-swatch" style={{ backgroundColor: hex }} />
      </span>
      <span className="gp-rte-palette">
        <button
          type="button"
          title="Default (theme)"
          className="gp-rte-swatch-clear"
          onClick={onClear}
        />
        {colors.map((color) => (
          <button
            key={color.hex}
            type="button"
            title={color.label}
            className={hex === color.hex ? "is-current" : ""}
            style={{ backgroundColor: color.hex }}
            onClick={() => onChange(color.hex)}
          />
        ))}
      </span>
    </span>
  );
}

function toTitleCase(text) {
  return text.replace(/\S+/g, (word) =>
    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
  );
}

function transformSelection(editor, transform) {
  const { from, to } = editor.state.selection;
  if (from === to) return;
  const text = editor.state.doc.textBetween(from, to, "\n");
  editor.chain().focus().insertContentAt({ from, to }, transform(text)).run();
}

function findRanges(editor, query) {
  if (!query) return [];
  const needle = query.toLowerCase();
  const ranges = [];
  editor.state.doc.descendants((node, pos) => {
    if (!node.isText || !node.text) return;
    const hay = node.text.toLowerCase();
    let idx = 0;
    while (idx < hay.length) {
      const found = hay.indexOf(needle, idx);
      if (found === -1) break;
      ranges.push({ from: pos + found, to: pos + found + query.length });
      idx = found + Math.max(needle.length, 1);
    }
  });
  return ranges;
}

function selectFindMatch(editor, query, fromPos, reverse) {
  const ranges = findRanges(editor, query);
  if (!ranges.length) return false;
  const match = reverse
    ? [...ranges].reverse().find((range) => range.from < fromPos) || ranges[ranges.length - 1]
    : ranges.find((range) => range.from >= fromPos) || ranges[0];
  editor.chain().focus().setTextSelection(match).scrollIntoView().run();
  return true;
}

function stripPastedImages(html) {
  return (html || "")
    .replace(/<img\b[^>]*>/gi, "")
    .replace(/<picture\b[\s\S]*?<\/picture>/gi, "")
    .replace(/<source\b[^>]*>/gi, "");
}

function shouldShowBubbleMenu(props) {
  const editor = props?.editor;
  if (!editor) return false;
  const selection = props?.state?.selection || editor.state?.selection;
  const from = selection?.from;
  const to = selection?.to;
  if (from == null || to == null || from === to) return false;
  return !editor.isActive("codeBlock");
}

function Toolbar({
  editor,
  fullscreen,
  showSource,
  showFind,
  onToggleFullscreen,
  onToggleSource,
  onToggleFind,
  onRequestLink,
  onRequestTable,
  onRequestListStart,
}) {
  if (!editor) return null;

  const inTable = editor.isActive("table");
  const fontSize = editor.getAttributes("textStyle").fontSize || "";
  const lineHeight = editor.getAttributes("textStyle").lineHeight || "";
  const textColor = editor.getAttributes("textStyle").color;
  const highlightColor = editor.getAttributes("highlight").color;
  const backgroundColor = editor.getAttributes("textStyle").backgroundColor;
  const canIndent = editor.can().indent();
  const canOutdent = editor.can().outdent();
  const currentIndent = Math.max(
    Number(editor.getAttributes("paragraph").indent) || 0,
    Number(editor.getAttributes("heading").indent) || 0,
  );
  const indentActive = currentIndent > 0;

  const bumpFontSize = (delta) => {
    const current = parseInt(fontSize || "16", 10);
    const next = Math.min(48, Math.max(10, current + delta));
    editor.chain().focus().setFontSize(`${next}px`).run();
  };

  return (
    <div className="gp-rte-toolbar flex flex-wrap items-center gap-0.5 border-b border-gray-200 bg-[#fbf8fb] px-2 py-1.5">
      <ToolbarButton
        title="Undo"
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Redo"
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 size={14} />
      </ToolbarButton>

      <span className={divider} />

      <select
        className={selectClass}
        value={currentHeading(editor)}
        onChange={(e) => {
          const value = e.target.value;
          if (value === "p") editor.chain().focus().setParagraph().run();
          else editor.chain().focus().toggleHeading({ level: Number(value) }).run();
        }}
      >
        <option value="p">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
        <option value="4">Heading 4</option>
        <option value="5">Heading 5</option>
        <option value="6">Heading 6</option>
      </select>

      <select
        className={`${selectClass} max-w-[72px]`}
        value={fontSize}
        onChange={(e) => {
          const value = e.target.value;
          if (!value) editor.chain().focus().unsetFontSize().run();
          else editor.chain().focus().setFontSize(value).run();
        }}
      >
        <option value="">Size</option>
        {FONT_SIZES.map((size) => (
          <option key={size} value={size}>
            {size.replace("px", "")}
          </option>
        ))}
      </select>
      <ToolbarButton title="Smaller text" onClick={() => bumpFontSize(-2)}>
        <span className="text-[10px] font-black">A-</span>
      </ToolbarButton>
      <ToolbarButton title="Larger text" onClick={() => bumpFontSize(2)}>
        <span className="text-[12px] font-black">A+</span>
      </ToolbarButton>

      <select
        className={`${selectClass} max-w-[78px]`}
        value={lineHeight}
        onChange={(e) => {
          const value = e.target.value;
          if (!value) editor.chain().focus().unsetLineHeight().run();
          else editor.chain().focus().setLineHeight(value).run();
        }}
      >
        <option value="">Line</option>
        {LINE_HEIGHTS.map((height) => (
          <option key={height} value={height}>
            {height}
          </option>
        ))}
      </select>

      <select
        className={`${selectClass} max-w-[88px]`}
        defaultValue=""
        onChange={(e) => {
          const value = e.target.value;
          e.target.value = "";
          if (value === "upper") transformSelection(editor, (text) => text.toUpperCase());
          if (value === "lower") transformSelection(editor, (text) => text.toLowerCase());
          if (value === "title") transformSelection(editor, toTitleCase);
        }}
      >
        <option value="">Case</option>
        <option value="upper">UPPER</option>
        <option value="lower">lower</option>
        <option value="title">Title Case</option>
      </select>

      <span className={divider} />

      <ToolbarButton
        title="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <UnderlineIcon size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Strikethrough"
        active={editor.isActive("strike")}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Subscript"
        active={editor.isActive("subscript")}
        onClick={() => editor.chain().focus().toggleSubscript().run()}
      >
        <SubIcon size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Superscript"
        active={editor.isActive("superscript")}
        onClick={() => editor.chain().focus().toggleSuperscript().run()}
      >
        <SuperIcon size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Inline code"
        active={editor.isActive("code")}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code size={14} />
      </ToolbarButton>

      <ColorControl
        title="Text color"
        icon={<span className="text-[11px] font-black leading-none">A</span>}
        value={textColor}
        fallback="#15173d"
        colors={TEXT_COLORS}
        onChange={(color) => editor.chain().focus().setColor(color).run()}
        onClear={() => editor.chain().focus().unsetColor().run()}
      />
      <ColorControl
        title="Highlight"
        icon={<Highlighter size={12} />}
        value={highlightColor}
        fallback="#fde68a"
        colors={HIGHLIGHT_COLORS}
        onChange={(color) =>
          editor.chain().focus().setHighlight({ color }).run()
        }
        onClear={() => editor.chain().focus().unsetHighlight().run()}
      />
      <ColorControl
        title="Background color"
        icon={<PaintBucket size={12} />}
        value={backgroundColor}
        fallback="#ffffff"
        colors={HIGHLIGHT_COLORS}
        onChange={(color) => editor.chain().focus().setBackgroundColor(color).run()}
        onClear={() => editor.chain().focus().unsetBackgroundColor().run()}
      />

      <span className={divider} />

      <ToolbarButton
        title="Align left"
        active={editor.isActive({ textAlign: "left" })}
        onClick={() => editor.chain().focus().setTextAlign("left").run()}
      >
        <AlignLeft size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Align center"
        active={editor.isActive({ textAlign: "center" })}
        onClick={() => editor.chain().focus().setTextAlign("center").run()}
      >
        <AlignCenter size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Align right"
        active={editor.isActive({ textAlign: "right" })}
        onClick={() => editor.chain().focus().setTextAlign("right").run()}
      >
        <AlignRight size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Justify"
        active={editor.isActive({ textAlign: "justify" })}
        onClick={() => editor.chain().focus().setTextAlign("justify").run()}
      >
        <AlignJustify size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Block LTR"
        active={editor.isActive({ dir: "ltr" })}
        onClick={() => editor.chain().focus().setTextDirection("ltr").run()}
      >
        <span className="text-[9px] font-black">LTR</span>
      </ToolbarButton>
      <ToolbarButton
        title="Block RTL"
        active={editor.isActive({ dir: "rtl" })}
        onClick={() => editor.chain().focus().setTextDirection("rtl").run()}
      >
        <span className="text-[9px] font-black">RTL</span>
      </ToolbarButton>

      <span className={divider} />

      <ToolbarButton
        title="Bullet list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Task list"
        active={editor.isActive("taskList")}
        onClick={() => editor.chain().focus().toggleTaskList().run()}
      >
        <ListChecks size={14} />
      </ToolbarButton>
      <ToolbarButton title="List start number" onClick={onRequestListStart}>
        <span className="text-[9px] font-black">1.</span>
      </ToolbarButton>
      <ToolbarButton
        title="Decrease indent"
        disabled={!canOutdent}
        onClick={() => editor.chain().focus().outdent().run()}
      >
        <IndentDecrease size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Increase indent"
        active={indentActive}
        disabled={!canIndent}
        onClick={() => editor.chain().focus().indent().run()}
      >
        <IndentIncrease size={14} />
      </ToolbarButton>

      <span className={divider} />

      <ToolbarButton
        title="Quote"
        active={editor.isActive("blockquote")}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Code block"
        active={editor.isActive("codeBlock")}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <Code size={13} />
      </ToolbarButton>
      <ToolbarButton
        title="Horizontal rule"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <Minus size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Line break"
        onClick={() => editor.chain().focus().setHardBreak().run()}
      >
        <WrapText size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Collapsible section"
        active={editor.isActive("details")}
        onClick={() =>
          editor.isActive("details")
            ? editor.chain().focus().unsetDetails().run()
            : editor.chain().focus().setDetails().run()
        }
      >
        <ListCollapse size={14} />
      </ToolbarButton>

      <span className={divider} />

      <ToolbarButton title="Link" active={editor.isActive("link")} onClick={onRequestLink}>
        <Link2 size={14} />
      </ToolbarButton>
      <ToolbarButton
        title="Remove link"
        disabled={!editor.isActive("link")}
        onClick={() => editor.chain().focus().unsetLink().run()}
      >
        <Link2Off size={14} />
      </ToolbarButton>

      <select
        className={`${selectClass} max-w-[84px]`}
        defaultValue=""
        onChange={(e) => {
          const value = e.target.value;
          e.target.value = "";
          if (value) editor.chain().focus().insertContent(value).run();
        }}
      >
        <option value="">Symbol</option>
        {SPECIAL_CHARS.map((item) => (
          <option key={item.label} value={item.value}>
            {item.label}
          </option>
        ))}
      </select>

      <span className={divider} />

      <ToolbarButton title="Insert table" onClick={onRequestTable}>
        <Table2 size={14} />
      </ToolbarButton>
      {inTable && (
        <>
          <ToolbarButton
            title="Add row"
            className="!min-w-0 px-1.5 text-[10px] font-bold"
            onClick={() => editor.chain().focus().addRowAfter().run()}
          >
            +Row
          </ToolbarButton>
          <ToolbarButton
            title="Add column"
            className="!min-w-0 px-1.5 text-[10px] font-bold"
            onClick={() => editor.chain().focus().addColumnAfter().run()}
          >
            +Col
          </ToolbarButton>
          <ToolbarButton
            title="Delete row"
            className="!min-w-0 px-1.5 text-[10px] font-bold"
            onClick={() => editor.chain().focus().deleteRow().run()}
          >
            −Row
          </ToolbarButton>
          <ToolbarButton
            title="Delete column"
            className="!min-w-0 px-1.5 text-[10px] font-bold"
            onClick={() => editor.chain().focus().deleteColumn().run()}
          >
            −Col
          </ToolbarButton>
          <ToolbarButton
            title="Header row"
            className="!min-w-0 px-1.5 text-[10px] font-bold"
            onClick={() => editor.chain().focus().toggleHeaderRow().run()}
          >
            Header
          </ToolbarButton>
          <ToolbarButton
            title="Header column"
            className="!min-w-0 px-1.5 text-[10px] font-bold"
            onClick={() => editor.chain().focus().toggleHeaderColumn().run()}
          >
            HCol
          </ToolbarButton>
          <ToolbarButton
            title="Merge cells"
            className="!min-w-0 px-1.5 text-[10px] font-bold"
            disabled={!editor.can().mergeCells()}
            onClick={() => editor.chain().focus().mergeCells().run()}
          >
            Merge
          </ToolbarButton>
          <ToolbarButton
            title="Split cell"
            className="!min-w-0 px-1.5 text-[10px] font-bold"
            disabled={!editor.can().splitCell()}
            onClick={() => editor.chain().focus().splitCell().run()}
          >
            Split
          </ToolbarButton>
          <ToolbarButton
            title="Delete table"
            className="!min-w-0 px-1.5 text-[10px] font-bold text-red-600"
            onClick={() => editor.chain().focus().deleteTable().run()}
          >
            Del
          </ToolbarButton>
        </>
      )}

      <span className={divider} />

      <ToolbarButton
        title="Clear formatting"
        onClick={() =>
          editor
            .chain()
            .focus()
            .unsetAllMarks()
            .unsetHighlight()
            .unsetColor()
            .unsetBackgroundColor()
            .unsetFontSize()
            .unsetLineHeight()
            .unsetTextAlign()
            .clearNodes()
            .run()
        }
      >
        <Eraser size={14} />
      </ToolbarButton>
      <ToolbarButton title="Find and replace" active={showFind} onClick={onToggleFind}>
        <Search size={14} />
      </ToolbarButton>
      <ToolbarButton title="HTML source" active={showSource} onClick={onToggleSource}>
        <FileCode2 size={14} />
      </ToolbarButton>
      <ToolbarButton title={fullscreen ? "Exit fullscreen" : "Fullscreen"} active={fullscreen} onClick={onToggleFullscreen}>
        {fullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
      </ToolbarButton>
    </div>
  );
}

function RichTextEditorComponent({
  value = "",
  onChange,
  placeholder = "Write here...",
  dir = "ltr",
  minHeight = 220,
  disabled = false,
  className = "",
}) {
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const findFromRef = useRef(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [showSource, setShowSource] = useState(false);
  const [sourceHtml, setSourceHtml] = useState("");
  const [showFind, setShowFind] = useState(false);
  const [findQuery, setFindQuery] = useState("");
  const [replaceQuery, setReplaceQuery] = useState("");
  const [promptConfig, setPromptConfig] = useState(null);

  const editor = useEditor({
    immediatelyRender: false,
    shouldRerenderOnTransaction: true,
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3, 4, 5, 6] },
        trailingNode: false,
        link: false,
      }),
      OptionalDir,
      Indent,
      TextStyleKit.configure({
        fontFamily: false,
      }),
      Highlight.configure({ multicolor: true }),
      Subscript,
      Superscript,
      TextAlign.configure({
        types: ["heading", "paragraph", "listItem", "taskItem"],
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: "https",
        HTMLAttributes: {
          rel: "noopener noreferrer nofollow",
        },
      }),
      TableKit.configure({
        table: { resizable: true },
      }),
      TaskList,
      TaskItem.configure({ nested: true }),
      Details.configure({ persist: false }),
      DetailsSummary,
      DetailsContent,
      Typography,
      Placeholder.configure({ placeholder }),
      CharacterCount,
    ],
    content: value || "",
    editorProps: {
      attributes: {
        dir,
        spellcheck: "true",
      },
      handlePaste: (_view, event) => {
        const items = Array.from(event.clipboardData?.items || []);
        const hasImage = items.some((item) => item.type.startsWith("image/"));
        const hasText = items.some(
          (item) => item.type === "text/html" || item.type === "text/plain",
        );
        if (hasImage && !hasText) {
          event.preventDefault();
          return true;
        }
        return false;
      },
      handleDrop: (_view, event) => {
        const files = Array.from(event.dataTransfer?.files || []);
        if (files.some((file) => file.type.startsWith("image/"))) {
          event.preventDefault();
          return true;
        }
        return false;
      },
      transformPastedHTML: stripPastedImages,
    },
    onUpdate: ({ editor: instance }) => {
      const html = instance.isEmpty ? "" : instance.getHTML();
      onChangeRef.current?.(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!disabled && !showSource);
  }, [editor, disabled, showSource]);

  useEffect(() => {
    if (!editor) return;
    const next = value || "";
    const current = editor.isEmpty ? "" : editor.getHTML();
    if (next === current) return;
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, value]);

  useEffect(() => {
    if (!editor) return;
    const root = editor.view?.dom;
    if (root) root.setAttribute("dir", dir === "rtl" ? "rtl" : "ltr");
  }, [editor, dir]);

  useEffect(() => {
    if (!fullscreen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKeyDown = (event) => {
      if (event.key === "Escape") setFullscreen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [fullscreen]);

  const applySource = () => {
    if (!editor) return;
    editor.commands.setContent(sourceHtml || "", { emitUpdate: true });
    setShowSource(false);
  };

  const runFind = (reverse = false) => {
    if (!editor || !findQuery) return;
    const from = reverse
      ? editor.state.selection.from
      : editor.state.selection.to || findFromRef.current;
    const found = selectFindMatch(editor, findQuery, reverse ? from : from, reverse);
    if (found) findFromRef.current = editor.state.selection.to;
  };

  const runReplace = () => {
    if (!editor || !findQuery) return;
    const { from, to } = editor.state.selection;
    const selected = editor.state.doc.textBetween(from, to);
    if (selected.toLowerCase() !== findQuery.toLowerCase()) {
      runFind(false);
      return;
    }
    editor.chain().focus().insertContentAt({ from, to }, replaceQuery).run();
    findFromRef.current = from + replaceQuery.length;
    runFind(false);
  };

  const runReplaceAll = () => {
    if (!editor || !findQuery) return;
    const ranges = findRanges(editor, findQuery);
    if (!ranges.length) return;
    editor
      .chain()
      .focus()
      .command(({ tr }) => {
        [...ranges].reverse().forEach((range) => {
          tr.insertText(replaceQuery, range.from, range.to);
        });
        return true;
      })
      .run();
  };

  const charCount = editor?.storage?.characterCount?.characters?.() ?? 0;
  const wordCount = editor?.storage?.characterCount?.words?.() ?? 0;

  const closePrompt = () => setPromptConfig(null);

  const applyLinkFromPrompt = (values) => {
    if (!editor) return;
    const url = (values.url || "").trim();
    if (!url) {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      closePrompt();
      return;
    }
    const external = /^https?:\/\//i.test(url);
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({
        href: url,
        target: external ? "_blank" : null,
        rel: external ? "noopener noreferrer nofollow" : null,
      })
      .run();
    closePrompt();
  };

  const openLinkPrompt = () => {
    if (!editor) return;
    const previous = editor.getAttributes("link").href || "https://";
    setPromptConfig({
      id: `link-${Date.now()}`,
      title: "Insert link",
      description: "Leave empty and apply to remove the link.",
      confirmLabel: "Apply link",
      fields: [
        {
          name: "url",
          label: "URL",
          defaultValue: previous,
          placeholder: "https://example.com",
        },
      ],
      onConfirm: applyLinkFromPrompt,
    });
  };

  const openTablePrompt = () => {
    if (!editor) return;
    setPromptConfig({
      id: `table-${Date.now()}`,
      title: "Insert table",
      description: "Choose rows and columns for the new table.",
      confirmLabel: "Insert table",
      fields: [
        {
          name: "rows",
          label: "Rows",
          type: "number",
          defaultValue: "3",
          min: 1,
          max: 20,
          required: true,
        },
        {
          name: "cols",
          label: "Columns",
          type: "number",
          defaultValue: "3",
          min: 1,
          max: 10,
          required: true,
        },
      ],
      onConfirm: (values) => {
        const rows = Math.min(20, Math.max(1, Number(values.rows) || 3));
        const cols = Math.min(10, Math.max(1, Number(values.cols) || 3));
        editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run();
        closePrompt();
      },
    });
  };

  const openListStartPrompt = () => {
    if (!editor || !editor.isActive("orderedList")) return;
    const current = editor.getAttributes("orderedList").start || 1;
    setPromptConfig({
      id: `list-start-${Date.now()}`,
      title: "List start number",
      description: "Set the starting number for this ordered list.",
      confirmLabel: "Update",
      fields: [
        {
          name: "start",
          label: "Start at",
          type: "number",
          defaultValue: String(current),
          min: 1,
          required: true,
        },
      ],
      onConfirm: (values) => {
        const start = Number(values.start);
        if (!Number.isFinite(start) || start < 1) return;
        editor.chain().focus().updateAttributes("orderedList", { start }).run();
        closePrompt();
      },
    });
  };

  const promptModal = promptConfig ? (
    <EditorPromptModal
      key={promptConfig.id}
      open
      title={promptConfig.title}
      description={promptConfig.description}
      confirmLabel={promptConfig.confirmLabel}
      fields={promptConfig.fields}
      onConfirm={promptConfig.onConfirm}
      onCancel={closePrompt}
    />
  ) : null;

  const editorNode = (
    <div
      className={`gp-rte overflow-hidden rounded-xl border border-gray-200 bg-white ${
        dir === "rtl" ? "is-rtl" : ""
      } ${fullscreen ? "is-fullscreen" : ""} ${className}`}
      style={{ "--rte-min-height": `${minHeight}px` }}
    >
      <Toolbar
        editor={editor}
        fullscreen={fullscreen}
        showSource={showSource}
        showFind={showFind}
        onToggleFullscreen={() => setFullscreen((open) => !open)}
        onToggleSource={() => {
          if (!editor) return;
          if (!showSource) {
            setSourceHtml(editor.isEmpty ? "" : editor.getHTML());
            setShowSource(true);
            return;
          }
          applySource();
        }}
        onToggleFind={() => setShowFind((open) => !open)}
        onRequestLink={openLinkPrompt}
        onRequestTable={openTablePrompt}
        onRequestListStart={openListStartPrompt}
      />

      {showFind && (
        <div className="gp-rte-find flex flex-wrap items-center gap-2 border-b border-gray-200 bg-white px-2 py-1.5">
          <input
            className="h-7 min-w-[140px] flex-1 rounded-md border border-gray-200 px-2 text-xs outline-none focus:border-[#982598]"
            placeholder="Find"
            value={findQuery}
            onChange={(e) => setFindQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                runFind(e.shiftKey);
              }
            }}
          />
          <input
            className="h-7 min-w-[140px] flex-1 rounded-md border border-gray-200 px-2 text-xs outline-none focus:border-[#982598]"
            placeholder="Replace"
            value={replaceQuery}
            onChange={(e) => setReplaceQuery(e.target.value)}
          />
          <button type="button" className="gp-rte-find-btn" onClick={() => runFind(true)}>
            Prev
          </button>
          <button type="button" className="gp-rte-find-btn" onClick={() => runFind(false)}>
            Next
          </button>
          <button type="button" className="gp-rte-find-btn" onClick={runReplace}>
            Replace
          </button>
          <button type="button" className="gp-rte-find-btn" onClick={runReplaceAll}>
            All
          </button>
        </div>
      )}

      {editor && !showSource && (
        <BubbleMenu
          editor={editor}
          className="gp-rte-bubble"
          shouldShow={shouldShowBubbleMenu}
        >
          <ToolbarButton
            title="Bold"
            active={editor.isActive("bold")}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <Bold size={13} />
          </ToolbarButton>
          <ToolbarButton
            title="Italic"
            active={editor.isActive("italic")}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <Italic size={13} />
          </ToolbarButton>
          <ToolbarButton
            title="Underline"
            active={editor.isActive("underline")}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <UnderlineIcon size={13} />
          </ToolbarButton>
          <ToolbarButton
            title="Link"
            active={editor.isActive("link")}
            onClick={openLinkPrompt}
          >
            <Link2 size={13} />
          </ToolbarButton>
        </BubbleMenu>
      )}

      {showSource ? (
        <div className="gp-rte-canvas flex flex-col">
          <textarea
            className="gp-rte-source"
            value={sourceHtml}
            onChange={(e) => setSourceHtml(e.target.value)}
            spellCheck={false}
          />
          <div className="flex justify-end gap-2 border-t border-gray-100 bg-[#fbf8fb] px-2 py-1.5">
            <button
              type="button"
              className="gp-rte-find-btn"
              onClick={() => setShowSource(false)}
            >
              Cancel
            </button>
            <button type="button" className="gp-rte-find-btn gp-rte-find-btn-primary" onClick={applySource}>
              Apply HTML
            </button>
          </div>
        </div>
      ) : (
        <div className="gp-rte-canvas">
          <EditorContent editor={editor} />
        </div>
      )}

      <div className="flex justify-end gap-3 border-t border-gray-100 bg-[#fbf8fb] px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
        <span>{wordCount} words</span>
        <span>{charCount} chars</span>
      </div>
    </div>
  );

  if (fullscreen) {
    return (
      <>
        <div className="gp-rte-fs-placeholder" style={{ minHeight }} />
        {createPortal(
          <div className="gp-rte-fs-root">
            <button
              type="button"
              className="gp-rte-fs-backdrop"
              aria-label="Exit fullscreen"
              onClick={() => setFullscreen(false)}
            />
            {editorNode}
          </div>,
          document.body,
        )}
        {promptModal}
      </>
    );
  }

  return (
    <>
      {editorNode}
      {promptModal}
    </>
  );
}

export const RichTextEditor = React.memo(
  RichTextEditorComponent,
  (prev, next) =>
    prev.value === next.value &&
    prev.dir === next.dir &&
    prev.disabled === next.disabled &&
    prev.placeholder === next.placeholder &&
    prev.minHeight === next.minHeight &&
    prev.className === next.className,
);

export default RichTextEditor;
